import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { CalendarClock, Video, KeyRound, Loader2, GraduationCap, Search, CalendarPlus } from "lucide-react";
import { Tx } from "../context/AutoTranslateContext";
import WhatsAppOptIn from "../components/WhatsAppOptIn";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const gcalLink = (title, startIso, minutes, details, location) => {
  try {
    const st = new Date(startIso);
    const en = new Date(st.getTime() + (minutes || 30) * 60000);
    const fmt = (d) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
    const q = new URLSearchParams({
      action: "TEMPLATE", text: title, dates: `${fmt(st)}/${fmt(en)}`,
      details: details || "", location: location || "",
    });
    return `https://calendar.google.com/calendar/render?${q.toString()}`;
  } catch { return ""; }
};

const SessionRow = ({ s }) => {
  const occ = (s.sessions || []).filter((o) => new Date(o.starts_at) >= new Date(Date.now() - 36e5));
  const calTitle = s.tutor_name ? `Qur'an & Arabic session with ${s.tutor_name}` : `Qur'an & Arabic session with ${s.student_name}`;
  return (
    <div data-testid="session-row" className="clay-surface rounded-2xl p-5">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <h3 className="font-heading text-forest font-semibold">{s.subject}</h3>
          <p className="text-xs text-mute">{s.tutor_name ? <>with {s.tutor_name}</> : s.student_name}</p>
        </div>
        <span className="text-[10px] uppercase tracking-widest bg-forest/10 text-forest px-2.5 py-1 rounded-full">{s.status}</span>
      </div>
      <ul className="space-y-2">
        {occ.length === 0 && <li className="text-xs text-mute italic"><Tx>No upcoming times.</Tx></li>}
        {occ.map((o, i) => (
          <li key={i} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm border-t border-forest/5 pt-2">
            <span className="inline-flex items-center gap-1.5 text-ink"><CalendarClock className="w-3.5 h-3.5 text-gold" />{new Date(o.starts_at).toLocaleString()}</span>
            <span className="inline-flex items-center gap-1.5 text-mute text-xs"><KeyRound className="w-3.5 h-3.5" />{o.video_room || s.video_room}</span>
            <div className="inline-flex items-center gap-2 ml-auto">
              <a data-testid="add-to-calendar-link" href={gcalLink(calTitle, o.starts_at, s.session_minutes, `Join: ${o.video_url || s.video_url} | Passcode: ${o.video_room || s.video_room}`, o.video_url || s.video_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-forest border border-forest/20 px-3 py-1.5 rounded-full hover:bg-forest/5">
                <CalendarPlus className="w-3.5 h-3.5" /> <Tx>Add to calendar</Tx>
              </a>
              <a href={o.video_url || s.video_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs bg-forest text-sand px-3 py-1.5 rounded-full hover:bg-sage">
                <Video className="w-3.5 h-3.5" /> <Tx>Join</Tx>
              </a>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

const MySessions = () => {
  const [email, setEmail] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    if (!email.trim().includes("@")) { setErr("Enter the email you booked/applied with."); return; }
    setErr(""); setBusy(true);
    try {
      const r = await axios.get(`${API}/tutoring/my-sessions`, { params: { email: email.trim() } });
      setData(r.data);
    } catch (e) { setErr(e?.response?.data?.detail || "Could not load sessions"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="my-sessions-page" className="max-w-3xl mx-auto px-4 sm:px-8 py-12">
      <div className="text-center mb-8">
        <div className="w-12 h-12 rounded-full bg-gold/15 text-gold flex items-center justify-center mx-auto mb-3"><GraduationCap className="w-6 h-6" /></div>
        <h1 className="font-heading text-3xl sm:text-4xl text-forest font-bold mb-2"><Tx>My Sessions</Tx></h1>
        <p className="text-sm text-mute"><Tx>Students and teachers — enter your email to see your upcoming sessions, times (in your local timezone) and access codes.</Tx></p>
      </div>

      <div className="flex gap-2 mb-6">
        <input data-testid="my-sessions-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()} placeholder="you@example.com"
          className="flex-1 bg-cream border border-forest/15 rounded-full px-5 py-3 text-sm outline-none focus:border-forest" />
        <button data-testid="my-sessions-load" onClick={load} disabled={busy}
          className="inline-flex items-center gap-2 bg-forest text-sand px-6 py-3 rounded-full text-sm hover:bg-sage disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} <Tx>Find</Tx>
        </button>
      </div>
      {err && <p className="text-xs text-clay bg-clay/10 rounded-full px-4 py-2 mb-4">{err}</p>}

      {data && (
        <div className="space-y-6">
          <WhatsAppOptIn />
          {data.is_teacher && (
            <section>
              <h2 className="text-[10px] uppercase tracking-widest text-gold mb-3"><Tx>Sessions you are teaching</Tx></h2>
              <div className="space-y-3">
                {data.as_teacher.length ? data.as_teacher.map((s) => <SessionRow key={s.id} s={s} />)
                  : <p className="text-sm text-mute italic"><Tx>No teaching sessions yet.</Tx></p>}
              </div>
            </section>
          )}
          <section>
            <h2 className="text-[10px] uppercase tracking-widest text-gold mb-3"><Tx>Your booked sessions</Tx></h2>
            <div className="space-y-3">
              {data.as_student.length ? data.as_student.map((s) => <SessionRow key={s.id} s={s} />)
                : <p className="text-sm text-mute italic"><Tx>No booked sessions found for this email.</Tx></p>}
            </div>
          </section>
          <p className="text-center text-xs text-mute">
            <Link to="/academy/tutors" className="text-forest hover:underline"><Tx>Book another session →</Tx></Link>
          </p>
        </div>
      )}
    </div>
  );
};

export default MySessions;
